<script>
    import { onMount } from 'svelte';
    import { getApiBase } from './apiBase.js';
    import { fetchAttendanceReport, vilniusWeek } from './attendanceReportApi.js';

    const apiBase = getApiBase();
    const defaults = vilniusWeek();
    const pageSize = 50;
    let startDate = defaults.startDate;
    let endDate = defaults.endDate;
    let report = null;
    let loading = false;
    let error = '';
    let page = 1;
    let expanded = {};

    function formatDateTime(value) {
        if (!value) return '—';
        return new Intl.DateTimeFormat('lt-LT', {
            timeZone: 'Europe/Vilnius', year: 'numeric', month: '2-digit',
            day: '2-digit', hour: '2-digit', minute: '2-digit'
        }).format(new Date(value));
    }

    function statusLabel(status) {
        return { attended: 'Attended', missing: 'Missing', pending: 'Pending', unresolved: 'Unresolved' }[status] || status;
    }

    function toggleClass(eventId) {
        expanded = { ...expanded, [eventId]: !expanded[eventId] };
    }

    async function loadReport(targetPage = 1) {
        loading = true;
        error = '';
        try {
            report = await fetchAttendanceReport({
                apiBase, token: localStorage.getItem('jwtToken'), startDate,
                endDate, page: targetPage, pageSize
            });
            page = report.pagination.page;
        } catch (err) {
            error = err.message || 'Failed to load class attendance.';
            report = null;
        } finally {
            loading = false;
        }
    }

    function submitRange() {
        expanded = {};
        loadReport(1);
    }

    onMount(() => loadReport(1));
</script>

<div class="attendance-report">
    <form class="filters" on:submit|preventDefault={submitRange}>
        <label><span>From</span><input type="date" bind:value={startDate} required /></label>
        <label><span>To</span><input type="date" bind:value={endDate} required /></label>
        <button type="submit" disabled={loading}>Show classes</button>
    </form>

    {#if loading}
        <div class="state">Loading attendance…</div>
    {:else if error}
        <div class="state error" role="alert">{error}</div>
    {:else if report && report.classes.length === 0}
        <div class="state">No active scheduled classes in this date range.</div>
    {:else if report}
        <div class="result-summary">{report.pagination.total_items} scheduled {report.pagination.total_items === 1 ? 'class' : 'classes'}</div>
        <div class="class-list">
            {#each report.classes as scheduledClass (scheduledClass.event_id)}
                <article class="class-card">
                    <button type="button" class="class-summary" aria-expanded={!!expanded[scheduledClass.event_id]} on:click={() => toggleClass(scheduledClass.event_id)}>
                        <span class="chevron" aria-hidden="true">{expanded[scheduledClass.event_id] ? '▾' : '▸'}</span>
                        <span class="class-identity">
                            <strong>{scheduledClass.name}</strong>
                            <small>{formatDateTime(scheduledClass.starts_at)} · {scheduledClass.teacher_label}</small>
                        </span>
                        <span class="counts">
                            <span class="count attended">{scheduledClass.counts.attended} attended</span>
                            {#if scheduledClass.counts.missing}<span class="count missing">{scheduledClass.counts.missing} missing</span>{/if}
                            {#if scheduledClass.counts.pending}<span class="count pending">{scheduledClass.counts.pending} pending</span>{/if}
                            {#if scheduledClass.counts.unresolved}<span class="count unresolved">{scheduledClass.counts.unresolved} unresolved</span>{/if}
                        </span>
                    </button>

                    {#if expanded[scheduledClass.event_id]}
                        <div class="participants">
                            <div class="participant header" aria-hidden="true"><span>Participant</span><span>Role</span><span>Status</span><span>Check-in</span></div>
                            {#each scheduledClass.participants as participant, index (`${participant.role}-${participant.user_id || index}`)}
                                <div class="participant">
                                    <span class="participant-name">{participant.name}{#if participant.assignment_warning}<small>{participant.assignment_warning}</small>{/if}</span>
                                    <span class="role">{participant.role}</span>
                                    <span><span class="status-badge" class:attended={participant.status === 'attended'} class:missing={participant.status === 'missing'} class:pending={participant.status === 'pending'} class:unresolved={participant.status === 'unresolved'}>{statusLabel(participant.status)}</span></span>
                                    <span>{formatDateTime(participant.checked_at)}</span>
                                </div>
                            {/each}
                        </div>
                    {/if}
                </article>
            {/each}
        </div>

        {#if report.pagination.total_pages > 1}
            <nav class="pagination" aria-label="Attendance report pages">
                <button type="button" disabled={!report.pagination.has_previous || loading} on:click={() => loadReport(page - 1)}>Previous</button>
                <span>Page {page} of {report.pagination.total_pages}</span>
                <button type="button" disabled={!report.pagination.has_next || loading} on:click={() => loadReport(page + 1)}>Next</button>
            </nav>
        {/if}
    {/if}
</div>

<style>
    .attendance-report { color: #1f2937; }
    .filters { display: flex; align-items: end; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
    label { display: grid; gap: 5px; font-size: 13px; font-weight: 600; }
    input { border: 1px solid #cbd5e1; border-radius: 7px; padding: 8px 10px; font: inherit; }
    button { border: 1px solid #2563eb; border-radius: 7px; background: #2563eb; color: white; padding: 8px 13px; cursor: pointer; font: inherit; }
    button:disabled { cursor: not-allowed; opacity: .55; }
    .state { padding: 20px; text-align: center; color: #64748b; }
    .state.error { color: #b91c1c; background: #fef2f2; border-radius: 8px; }
    .result-summary { margin-bottom: 10px; color: #64748b; font-size: 13px; }
    .class-list { display: grid; gap: 10px; }
    .class-card { border: 1px solid #e2e8f0; border-radius: 10px; overflow: hidden; background: white; }
    .class-summary { width: 100%; display: grid; grid-template-columns: auto minmax(220px, 1fr) auto; align-items: center; gap: 10px; border: 0; border-radius: 0; background: white; color: inherit; text-align: left; padding: 13px; }
    .class-summary:hover { background: #f8fafc; }
    .chevron { color: #64748b; }
    .class-identity { display: grid; gap: 3px; }
    .class-identity small { color: #64748b; font-weight: 400; }
    .counts { display: flex; gap: 6px; flex-wrap: wrap; justify-content: end; }
    .count, .status-badge { border-radius: 999px; padding: 3px 8px; font-size: 12px; font-weight: 700; white-space: nowrap; }
    .attended { color: #166534; background: #dcfce7; }
    .missing { color: #991b1b; background: #fee2e2; }
    .pending { color: #92400e; background: #fef3c7; }
    .unresolved { color: #5b21b6; background: #ede9fe; }
    .participants { border-top: 1px solid #e2e8f0; }
    .participant { display: grid; grid-template-columns: minmax(180px, 1.4fr) .6fr .7fr 1fr; gap: 10px; align-items: center; padding: 10px 13px; border-top: 1px solid #f1f5f9; font-size: 13px; }
    .participant:first-child { border-top: 0; }
    .participant.header { background: #f8fafc; color: #64748b; font-size: 11px; font-weight: 700; text-transform: uppercase; }
    .participant-name { display: grid; }
    .participant-name small { color: #7c3aed; }
    .role { text-transform: capitalize; }
    .pagination { display: flex; align-items: center; justify-content: center; gap: 14px; margin-top: 16px; }
    @media (max-width: 720px) {
        .class-summary { grid-template-columns: auto 1fr; }
        .counts { grid-column: 2; justify-content: start; }
        .participant, .participant.header { grid-template-columns: 1fr .7fr .8fr; }
        .participant > :last-child, .participant.header > :last-child { grid-column: 1 / -1; }
    }
</style>
